import React, { useEffect, useState } from "react";
import { api, setToken, getToken } from "@/api";
import { JerseyBadge } from "@/components/Shared";
import ScoreEditor from "@/components/ScoreEditor";
import { LoginScreen } from "@/components/LoginScreen";
import { Trash2, Radio, Clock, Flag, ChevronsRight, Square, Users, ClipboardList, RotateCcw } from "lucide-react";

export default function Setup() {
  const [divisions, setDivisions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [riderName, setRiderName] = useState("");
  const [riderShirt, setRiderShirt] = useState("");
  const [msg, setMsg] = useState("");
  const [advance, setAdvance] = useState({});
  const [me, setMe] = useState(null);
  const [checking, setChecking] = useState(true);
  const [regs, setRegs] = useState([]);
  const [regFilter, setRegFilter] = useState("all");
  const [openReg, setOpenReg] = useState(null);
  const [eventLive, setEventLive] = useState(false);
  const [resultsRevealed, setResultsRevealed] = useState(false);
  const [fund, setFund] = useState(null);
  const [donations, setDonations] = useState([]);
  const [showDonors, setShowDonors] = useState(false);
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ email: "", password: "", name: "", role: "judge", judge_num: 1 });
  const [pw, setPw] = useState({ current: "", next: "" });
  const [volunteers, setVolunteers] = useState([]);
  const [workshopSignups, setWorkshopSignups] = useState([]);
  const [workshopInfo, setWorkshopInfo] = useState(null);
  const [workshopCapInput, setWorkshopCapInput] = useState("");
  const [finance, setFinance] = useState(null);
  const [newFin, setNewFin] = useState({ kind: "expense", description: "", category: "Shirts", amount: "", date: "", note: "" });
  const [budgetInput, setBudgetInput] = useState("");
  const [wishlistUrl, setWishlistUrl] = useState("");
  const [bluevineUrl, setBluevineUrl] = useState("");
  const [donorName, setDonorName] = useState("");
  const [donorAmount, setDonorAmount] = useState("");
  const [schedPlan, setSchedPlan] = useState(null);
  const [schedStart, setSchedStart] = useState("");
  const [schedHeatMin, setSchedHeatMin] = useState(15);
  const [schedBreakMin, setSchedBreakMin] = useState(1);
  const [checkinData, setCheckinData] = useState(null);
  const [waitlistData, setWaitlistData] = useState(null);
  const [schedPlanAt, setSchedPlanAt] = useState(0);
  const [schedNow, setSchedNow] = useState(Date.now());

  const saveWishlist = async () => {
    await api.setWishlist(wishlistUrl.trim());
    flash("Wishlist link saved");
  };

  const saveBluevine = async () => {
    await api.setBluevine(bluevineUrl.trim());
    flash("Bluevine payment link saved");
  };

  const recordDonation = async () => {
    const amt = Number(donorAmount);
    if (!(amt > 0)) { flash("Enter a donation amount"); return; }
    await api.recordDonation({ donor_name: donorName.trim() || "Anonymous", amount: amt });
    flash(`Recorded $${amt.toFixed(0)} donation to the fund`);
    setDonorName(""); setDonorAmount("");
    loadDonations();
    loadFinance();
    api.sponsorshipStatus().then(setFund).catch(() => {});
  };

  const removeDonation = async (id) => {
    if (!window.confirm("Remove this donation from the fund?")) return;
    await api.deleteDonation(id);
    flash("Donation removed");
    loadDonations();
    loadFinance();
    api.sponsorshipStatus().then(setFund).catch(() => {});
  };

  const saveBudget = async () => {
    const amt = Number(budgetInput);
    if (!(amt >= 0)) { flash("Enter a valid budget"); return; }
    await api.setBudget(amt);
    flash("Budget saved");
    setBudgetInput("");
    loadFinance();
  };

  const EXPENSE_CATS = ["Shirts", "Permits", "Tents/Equipment", "Prizes/Awards", "Food/Hydration", "Marketing", "Water Safety", "Other"];
  const INCOME_CATS = ["Gate Cash", "Grant", "Sponsor Cash", "Merch", "Donation", "Other"];

  const loadFinance = async () => setFinance(await api.financeSummary());

  const addFinance = async () => {
    if (!newFin.description.trim() || !(Number(newFin.amount) > 0)) { flash("Add a description and amount"); return; }
    try {
      await api.addFinanceEntry({ ...newFin, amount: Number(newFin.amount) });
      flash("Entry added");
      setNewFin({ kind: newFin.kind, description: "", category: newFin.kind === "expense" ? "Shirts" : "Gate Cash", amount: "", date: "", note: "" });
      loadFinance();
    } catch (e) {
      flash(e?.response?.data?.detail || "Could not add entry");
    }
  };

  const removeFinance = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    await api.deleteFinanceEntry(id);
    flash("Entry deleted");
    loadFinance();
  };

  const exportFinance = () => {
    if (!finance) return;
    const rows = [
      { Date: "", Type: "AUTO", Category: "Entry fees collected", Description: "Paid entries (app)", Amount: finance.entries_income },
      { Date: "", Type: "AUTO", Category: "Sponsor donations", Description: "Community fund", Amount: finance.donations_income },
      ...finance.entries.map((e) => ({ Date: e.date, Type: e.kind, Category: e.category, Description: e.description, Amount: (e.kind === "expense" ? -e.amount : e.amount) })),
      { Date: "", Type: "TOTAL", Category: "", Description: `Income ${finance.total_income} · Expenses ${finance.total_expenses}`, Amount: finance.net },
    ];
    downloadCsv("pounders-finances.csv", rows);
  };

  const loadVolunteers = async () => setVolunteers(await api.volunteers());

  const loadWorkshop = async () => {
    setWorkshopSignups(await api.workshopSignups());
    setWorkshopInfo(await api.workshopInfo());
  };

  const saveWorkshopCap = async () => {
    const cap = Number(workshopCapInput);
    if (!(cap >= 0)) { flash("Enter a valid capacity"); return; }
    await api.setWorkshopCapacity(cap);
    flash("Workshop capacity saved");
    setWorkshopCapInput("");
    loadWorkshop();
  };

  const removeWorkshopSignup = async (id) => {
    if (!window.confirm("Remove this garden reservation?")) return;
    await api.deleteWorkshopSignup(id);
    flash("Reservation removed");
    loadWorkshop();
  };

  const exportWorkshop = () => downloadCsv("pounders-bucket-garden.csv", workshopSignups.map((s) => ({
    Family: s.name, Email: s.email, Phone: s.phone || "", Attending: s.party_size,
    Note: s.note || "", ReservedAt: s.created_at,
  })));

  const downloadCsv = (filename, rows) => {
    if (!rows.length) { flash("Nothing to export yet"); return; }
    const headers = Object.keys(rows[0]);
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [headers.join(","), ...rows.map((r) => headers.map((h) => esc(r[h])).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const exportRegistrations = () => downloadCsv("pounders-registrations.csv", regs.map((r) => ({
    Name: r.full_name, Age: r.age, Gender: r.gender, Email: r.email, Phone: r.phone,
    Divisions: (r.division_names || []).join(" / "), Shirt: r.shirt_size,
    Payment: r.payment_status, Method: r.payment_method,
    AmountDue: r.amount_due, Sponsored: r.sponsorship_applied || 0,
    Guardian: r.guardian_name || "", GuardianPhone: r.guardian_phone || "",
    Emergency: `${r.emergency_contact_name || ""} ${r.emergency_contact_phone || ""}`.trim(),
    Registered: r.created_at,
  })));

  const exportVolunteers = () => downloadCsv("pounders-volunteers.csv", volunteers.map((v) => ({
    Name: v.name, Email: v.email, Phone: v.phone, Status: v.status || "pending",
    Shifts: (v.shift_labels || []).join(" | "), Notes: v.notes || "", SignedUp: v.created_at,
  })));

  const removeVolunteer = async (id) => {
    if (!window.confirm("Remove this volunteer?")) return;
    await api.deleteVolunteer(id);
    flash("Volunteer removed");
    loadVolunteers();
  };

  const approveVolunteer = async (id) => {
    await api.approveVolunteer(id);
    flash("Volunteer confirmed — approval email sent");
    loadVolunteers();
  };

  const SHIRT_SIZES = ["Youth S", "Youth M", "Youth L", "Adult S", "Adult M", "Adult L", "Adult XL", "Adult 2XL"];

  const sendShirtReminders = async () => {
    const missing = regs.filter((r) => !r.shirt_size).length + volunteers.filter((v) => !v.shirt_size).length;
    if (missing === 0) { flash("Everyone has a shirt size — nothing to send"); return; }
    if (!window.confirm(`Email ${missing} people who are missing a shirt size?`)) return;
    try {
      const res = await api.shirtRemind(window.location.origin);
      flash(`Reminders sent: ${res.sent}${res.failed ? ` · ${res.failed} failed` : ""}`);
    } catch (e) {
      flash(e?.response?.data?.detail || "Could not send reminders");
    }
  };

  const exportShirts = () => {
    const rows = SHIRT_SIZES.map((sz) => ({
      Size: sz,
      Participants: regs.filter((r) => r.shirt_size === sz).length,
      Volunteers: volunteers.filter((v) => v.shirt_size === sz).length,
      Total: regs.filter((r) => r.shirt_size === sz).length + volunteers.filter((v) => v.shirt_size === sz).length,
    }));
    rows.push({
      Size: "MISSING",
      Participants: regs.filter((r) => !r.shirt_size).length,
      Volunteers: volunteers.filter((v) => !v.shirt_size).length,
      Total: regs.filter((r) => !r.shirt_size).length + volunteers.filter((v) => !v.shirt_size).length,
    });
    downloadCsv("pounders-shirt-orders.csv", rows);
  };

  const exportShirtsByRole = async () => {
    const data = await api.shirtsByRole();
    if (!data.rows.length) { flash("No shirt sizes collected yet"); return; }
    const rows = data.rows.map((r) => {
      const o = { Role_Color: r.role };
      data.sizes.forEach((s) => { o[s] = r.sizes[s] || 0; });
      o.Total = r.total;
      return o;
    });
    downloadCsv("pounders-shirts-by-role.csv", rows);
  };

  const authed = !!me;

  const sponsorReg = async (id) => {
    if (!window.confirm("Cover this competitor's entry fee from the community sponsorship fund?")) return;
    try {
      const res = await api.sponsorRegistration(id);
      flash(`Sponsored — $${Number(res.deducted).toFixed(0)} from the fund`);
      loadRegs();
      loadFund();
      loadDivisions();
      if (selected) loadDetail(selected);
    } catch (e) {
      flash(e?.response?.data?.detail || "Could not sponsor");
    }
  };

  const loadFund = async () => setFund(await api.sponsorshipStatus());
  const loadDonations = async () => setDonations(await api.donations());
  const loadUsers = async () => setUsers(await api.listUsers());

  const toggleLive = async () => {
    const res = await api.setEventLive(!eventLive);
    setEventLive(res.event_live);
    flash(res.event_live ? "Contest is now LIVE" : "Contest set to not-live");
  };

  const toggleReveal = async () => {
    const next = !resultsRevealed;
    if (next && !window.confirm("Reveal final results to the public now? Do this at the closing ceremony.")) return;
    const res = await api.revealResults(next);
    setResultsRevealed(res.results_revealed);
    flash(res.results_revealed ? "🏆 Final results are now PUBLIC" : "Final results hidden again");
  };

  const toLocalInput = (iso) => {
    const d = new Date(iso);
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  };
  const fmtClock = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); } catch (e) { return "—"; }
  };
  const applyPlan = (p) => { setSchedPlan(p); setSchedPlanAt(Date.now()); };
  const loadSchedule = async () => {
    const p = await api.schedulePlan();
    applyPlan(p);
    setSchedHeatMin(p.heat_duration_min);
    setSchedBreakMin(p.break_duration_min);
    if (p.start_at) setSchedStart((prev) => prev || toLocalInput(p.start_at));
  };
  const saveSchedConfig = async () => {
    if (!schedStart) { flash("Pick a start time first"); return; }
    const p = await api.setScheduleConfig({
      start_at: new Date(schedStart).toISOString(),
      heat_duration_min: Number(schedHeatMin),
      break_duration_min: Number(schedBreakMin),
    });
    applyPlan(p);
    flash("Schedule settings saved");
  };
  const startSched = async () => {
    if (!schedStart) { flash("Set a start time first"); return; }
    await saveSchedConfig();
    const p = await api.startSchedule();
    applyPlan(p);
    setEventLive(true);
    flash("▶ Schedule started — heats run automatically");
  };
  const pauseSched = async () => { applyPlan(await api.pauseSchedule()); flash("⏸ Schedule paused"); };
  const resumeSched = async () => { applyPlan(await api.resumeSchedule()); flash("▶ Schedule resumed"); };
  const skipSched = async () => {
    if (!window.confirm("Skip the current heat and jump to the next one now?")) return;
    applyPlan(await api.skipHeat());
    loadDivisions();
    flash("⏭ Skipped to next heat");
  };
  const stopSched = async () => {
    if (!window.confirm("Stop the auto schedule? Heats will no longer advance on their own.")) return;
    applyPlan(await api.stopSchedule());
    setEventLive(false);
    flash("Schedule stopped");
  };

  const approveReg = async (id) => {
    await api.approveRegistration(id);
    flash("Payment confirmed — added to heats");
    loadRegs();
    loadDivisions();
    loadFinance();
    if (selected) loadDetail(selected);
  };

  const buildAll = async () => {
    const res = await api.buildAllHeats();
    flash(`Placed competitors into heats (${res.divisions_built} divisions${res.divisions_locked ? `, ${res.divisions_locked} locked` : ""})`);
    loadDivisions();
    if (selected) loadDetail(selected);
  };

  const loadDivisions = async () => setDivisions(await api.divisions());
  const loadConfig = async () => {
    const c = await api.config();
    setEventLive(!!c.event_live);
    setResultsRevealed(!!c.results_revealed);
    setWishlistUrl(c.amazon_wishlist_url || "");
    setBluevineUrl(c.bluevine_url || "");
  };
  const loadRegs = async () => setRegs(await api.registrations());
  const loadDetail = async (id) => setDetail(await api.division(id));
  const loadCheckin = async () => setCheckinData(await api.checkin());
  const loadWaitlist = async () => setWaitlistData(await api.waitlist());
  const toggleCheckin = async (riderId, current, next) => {
    const status = current === next ? "none" : next;
    await api.setCheckin(riderId, status);
    loadCheckin();
  };
  const promote = async (regId, divisionId, name, divName) => {
    if (!window.confirm(`Promote ${name} off the waitlist into ${divName}? They'll be placed now and marked unpaid (settle payment in person).`)) return;
    await api.promoteWaitlist(regId, divisionId);
    flash(`${name} promoted into ${divName}`);
    loadWaitlist();
    loadCheckin();
    loadRegs();
    loadDivisions();
  };

  useEffect(() => {
    if (getToken()) {
      api.me().then((u) => { if (u.role === "admin") setMe(u); }).catch(() => setToken("")).finally(() => setChecking(false));
    } else {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    if (!authed) return;
    api.seed().then(loadDivisions);
    loadConfig();
    loadRegs();
    loadFund();
    loadDonations();
    loadUsers();
    loadVolunteers();
    loadFinance();
    loadWorkshop();
    loadCheckin();
    loadWaitlist();
  }, [authed]);

  useEffect(() => {
    if (!authed) return;
    loadSchedule();
    const id = setInterval(loadSchedule, 5000);
    return () => clearInterval(id);
  }, [authed]);

  useEffect(() => {
    const id = setInterval(() => setSchedNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const logout = () => {
    setToken("");
    setMe(null);
    setDetail(null);
    setSelected(null);
  };

  const createUser = async () => {
    try {
      const body = { ...newUser };
      if (body.role !== "judge") body.judge_num = null;
      await api.createUser(body);
      flash("Account created");
      setNewUser({ email: "", password: "", name: "", role: "judge", judge_num: 1 });
      loadUsers();
    } catch (e) {
      flash(e?.response?.data?.detail || "Could not create account");
    }
  };

  const removeUser = async (id) => {
    if (!window.confirm("Delete this account?")) return;
    try {
      await api.deleteUser(id);
      flash("Account deleted");
      loadUsers();
    } catch (e) {
      flash(e?.response?.data?.detail || "Could not delete");
    }
  };

  const changePassword = async () => {
    try {
      await api.changePassword(pw.current, pw.next);
      flash("Password updated");
      setPw({ current: "", next: "" });
    } catch (e) {
      flash(e?.response?.data?.detail || "Could not change password");
    }
  };

  const openDivision = async (id) => {
    setSelected(id);
    await loadDetail(id);
  };

  const flash = (t) => { setMsg(t); setTimeout(() => setMsg(""), 1500); };

  const addRider = async () => {
    if (!riderName.trim()) { flash("Enter a rider name"); return; }
    if (!riderShirt) { flash("Pick a shirt size so we don't miss any shirts"); return; }
    try {
      await api.addRider(selected, riderName.trim(), riderShirt);
      setRiderName("");
      setRiderShirt("");
      loadDetail(selected);
      loadDivisions();
    } catch (e) {
      flash(e?.response?.data?.detail || "Could not add rider");
    }
  };

  const refresh = () => { loadDetail(selected); loadDivisions(); };

  const deleteReg = async (id) => {
    if (!window.confirm("Remove this registration? This also removes them from their divisions.")) return;
    await api.deleteRegistration(id);
    flash("Registration removed");
    loadRegs();
    loadDivisions();
    loadFinance();
    if (selected) loadDetail(selected);
  };

  const doAdvance = async (roundId) => {
    try {
      const res = await api.advanceRound(selected, roundId, { advance_count: advance[roundId] ?? 2 });
      flash(`${res.round_name} built (${res.heats} heat${res.heats > 1 ? "s" : ""})`);
      refresh();
    } catch (e) {
      flash(e?.response?.data?.detail || "Could not advance round");
    }
  };

  if (checking) {
    return <div className="max-w-md mx-auto p-4 pt-20 text-center font-display uppercase tracking-widest text-gray-400" data-testid="setup-checking">Loading…</div>;
  }

  if (!authed) {
    return (
      <LoginScreen
        title="Admin Login"
        subtitle="Sign in with your organizer account"
        requiredRole="admin"
        onSuccess={(u) => { if (u.role === "admin") setMe(u); else { setToken(""); } }}
      />
    );
  }

  const schedRemaining = (() => {
    if (!schedPlan || !schedPlan.running || schedPlan.current_remaining_sec == null) return null;
    let rem = schedPlan.current_remaining_sec;
    if (!schedPlan.paused) rem = Math.max(0, rem - Math.floor((schedNow - schedPlanAt) / 1000));
    return rem;
  })();
  const fmtMMSS = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  return (
    <div className="max-w-3xl mx-auto p-4 pb-24" data-testid="setup-view">
      <div className="flex items-center justify-between mb-1">
        <h1 className="font-display text-4xl font-black uppercase tracking-tight">Setup</h1>
        <button data-testid="admin-lock-btn" onClick={logout} className="font-display text-xs uppercase tracking-widest underline text-gray-500">
          Log out
        </button>
      </div>
      <p className="font-display text-gray-500 uppercase text-xs tracking-widest mb-6">Signed in as {me.email}</p>

      {regs.filter((r) => r.payment_status === "sponsor_pending").length > 0 && (
        <button
          data-testid="pending-sponsor-banner"
          onClick={() => document.querySelector('[data-testid="registrations-panel"]')?.scrollIntoView({ behavior: "smooth" })}
          className="w-full brutal bg-[#E63946] text-white p-4 mb-6 flex items-center justify-between gap-3 active:scale-[0.99] transition-transform"
        >
          <span className="font-display font-black uppercase tracking-tight text-left">
            {regs.filter((r) => r.payment_status === "sponsor_pending").length} sponsorship request{regs.filter((r) => r.payment_status === "sponsor_pending").length > 1 ? "s" : ""} awaiting your approval
          </span>
          <span className="brutal-2 bg-white text-[#E63946] font-score text-3xl px-3" data-testid="pending-sponsor-count">
            {regs.filter((r) => r.payment_status === "sponsor_pending").length}
          </span>
        </button>
      )}

      {/* Go Live */}
      <div className="brutal p-4 mb-6" data-testid="go-live-panel">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-display text-xl font-black uppercase tracking-tight">Contest Status</h2>
            <p className="font-display text-xs text-gray-500">
              {eventLive ? "LIVE — Watch & Judges tabs are visible to everyone." : "Not live — Watch & Judges are hidden from the public."}
            </p>
          </div>
          <button
            data-testid="toggle-live-btn"
            onClick={toggleLive}
            className={`brutal font-display font-black uppercase tracking-widest px-6 py-3 active:scale-95 transition-transform ${eventLive ? "bg-[#E63946] text-white" : "bg-green-600 text-white"}`}
          >
            {eventLive ? "● End Live" : "▶ Go Live"}
          </button>
        </div>
        <div className="flex items-center justify-between gap-3 flex-wrap mt-4 pt-4 border-t-2 border-black">
          <div>
            <h3 className="font-display text-lg font-black uppercase tracking-tight">Final Results</h3>
            <p className="font-display text-xs text-gray-500">
              {resultsRevealed
                ? "REVEALED — finals are public on the Awards page & shareable."
                : "Hidden — only you & judges see finals. Reveal at the closing ceremony."}
            </p>
          </div>
          <button
            data-testid="toggle-reveal-btn"
            onClick={toggleReveal}
            className={`brutal font-display font-black uppercase tracking-widest px-6 py-3 active:scale-95 transition-transform ${resultsRevealed ? "bg-[#E63946] text-white" : "bg-[#FFD700] text-black"}`}
          >
            {resultsRevealed ? "🔒 Hide Results" : "🏆 Reveal Results"}
          </button>
        </div>
      </div>

      {/* Live Schedule (auto-run) */}
      <div className="brutal p-4 mb-6" data-testid="schedule-panel">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
          <div>
            <h2 className="font-display text-xl font-black uppercase tracking-tight">Live Schedule (Auto-Run)</h2>
            <p className="font-display text-xs text-gray-500">
              Runs each division's Round-1 heats together (all Menehune, then all Juniors, …), then Round 2 for every division, and so on — {schedHeatMin}-min heats with a {schedBreakMin}-min break, continuously until you pause.
            </p>
          </div>
          <span
            data-testid="schedule-status-badge"
            className={`brutal-2 font-display font-black uppercase text-xs tracking-widest px-3 py-1 ${
              !schedPlan?.running ? "bg-gray-200 text-black" : schedPlan?.paused ? "bg-[#FFD700] text-black" : "bg-green-600 text-white"
            }`}
          >
            {!schedPlan?.running ? "Idle" : schedPlan?.paused ? "⏸ Paused" : "● Running"}
          </span>
        </div>

        {schedPlan?.running && schedRemaining != null && (
          <div className="brutal-2 bg-black text-white p-3 mb-3 flex items-center justify-between" data-testid="schedule-live-timer">
            <div className="font-display text-[10px] uppercase tracking-[0.25em] text-gray-400">
              {schedPlan.phase === "break" ? "Break — next heat in" : "Live heat — time left"}
            </div>
            <div className={`font-score text-4xl leading-none ${schedPlan.paused ? "text-[#FFD700]" : (schedPlan.phase === "break" ? "text-gray-300" : "text-[#FFD700]")}`} data-testid="schedule-live-timer-value">
              {fmtMMSS(schedRemaining)}{schedPlan.paused ? " ⏸" : ""}
            </div>
          </div>
        )}

        <div className="grid sm:grid-cols-3 gap-2 mb-3">
          <div>
            <label className="font-display text-[10px] uppercase tracking-widest text-gray-500 block mb-1">Start time</label>
            <input
              data-testid="schedule-start-input"
              type="datetime-local"
              value={schedStart}
              onChange={(e) => setSchedStart(e.target.value)}
              className="w-full brutal-2 p-2 font-display text-sm focus:outline-none bg-white"
            />
          </div>
          <div>
            <label className="font-display text-[10px] uppercase tracking-widest text-gray-500 block mb-1">Heat mins</label>
            <input
              data-testid="schedule-heat-min-input"
              type="number"
              min="1"
              max="120"
              value={schedHeatMin}
              onChange={(e) => setSchedHeatMin(e.target.value)}
              className="w-full brutal-2 p-2 font-display text-sm focus:outline-none bg-white"
            />
          </div>
          <div>
            <label className="font-display text-[10px] uppercase tracking-widest text-gray-500 block mb-1">Break mins</label>
            <input
              data-testid="schedule-break-min-input"
              type="number"
              min="0"
              max="60"
              value={schedBreakMin}
              onChange={(e) => setSchedBreakMin(e.target.value)}
              className="w-full brutal-2 p-2 font-display text-sm focus:outline-none bg-white"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-3">
          <button data-testid="schedule-save-btn" onClick={saveSchedConfig} className="brutal-2 bg-white font-display font-black uppercase text-xs px-4 py-2">Save Settings</button>
          {!schedPlan?.running ? (
            <button data-testid="schedule-start-btn" onClick={startSched} className="brutal bg-green-600 text-white font-display font-black uppercase tracking-widest px-6 py-2 active:scale-95 transition-transform">▶ Start Schedule</button>
          ) : (
            <>
              {schedPlan?.paused ? (
                <button data-testid="schedule-resume-btn" onClick={resumeSched} className="brutal bg-green-600 text-white font-display font-black uppercase tracking-widest px-6 py-2 active:scale-95 transition-transform">▶ Resume</button>
              ) : (
                <button data-testid="schedule-pause-btn" onClick={pauseSched} className="brutal bg-[#FFD700] text-black font-display font-black uppercase tracking-widest px-6 py-2 active:scale-95 transition-transform">⏸ Pause</button>
              )}
              <button data-testid="schedule-skip-btn" onClick={skipSched} className="brutal-2 bg-white font-display font-black uppercase text-xs px-4 py-2">⏭ Skip Heat</button>
              <button data-testid="schedule-stop-btn" onClick={stopSched} className="brutal bg-[#E63946] text-white font-display font-black uppercase tracking-widest px-4 py-2 active:scale-95 transition-transform">■ Stop</button>
            </>
          )}
          <a
            data-testid="announcer-link"
            href="/announcer"
            target="_blank"
            rel="noopener noreferrer"
            className="brutal-2 bg-black text-white font-display font-black uppercase text-xs px-4 py-2 flex items-center"
          >
            📺 Announcer Screen ↗
          </a>
        </div>

        {schedPlan?.paused && (
          <p className="font-display text-xs text-gray-600 mb-2">
            💡 Paused — you can now reset/redo any heat below to fix scores, then Resume.
          </p>
        )}

        {schedPlan && schedPlan.total_heats > 0 ? (
          <div className="brutal-2 bg-gray-50 p-2" data-testid="schedule-panel-list">
            <div className="font-display text-[10px] uppercase tracking-widest text-gray-500 mb-1">
              {schedPlan.total_heats} heats · showing running order
            </div>
            <div className="space-y-1 max-h-64 overflow-y-auto pr-1">
              {schedPlan.heats.map((h) => (
                <div key={h.id} className={`flex items-center gap-2 text-sm px-1 py-0.5 ${h.status === "live" ? "bg-red-100" : h.status === "done" ? "opacity-50" : ""}`}>
                  <span className="font-score text-base w-14 shrink-0">{fmtClock(h.scheduled_at)}</span>
                  <span className="font-display font-bold uppercase truncate flex-1 min-w-0">{h.division_name} · {h.name}</span>
                  <span className={`font-display text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 shrink-0 ${
                    h.status === "live" ? "bg-[#E63946] text-white" : h.status === "on_deck" ? "bg-[#FFD700] text-black" : h.status === "done" ? "bg-black text-white" : "text-gray-400"
                  }`}>{h.status === "on_deck" ? "on deck" : h.status}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="font-display text-xs text-gray-500" data-testid="schedule-no-heats">No heats yet — generate/build heats below first, then start the schedule.</p>
        )}
      </div>

      {/* Amazon Wishlist link */}
      <div className="brutal p-4 mb-6" data-testid="wishlist-panel">
        <h2 className="font-display text-xl font-black uppercase tracking-tight mb-1">Amazon Wishlist</h2>
        <p className="font-display text-xs text-gray-500 mb-2">Paste your Amazon wishlist link — a "Gear Wishlist" section appears on the public Sponsor page so people can buy supplies for the event.</p>
        <div className="flex items-center gap-2">
          <input data-testid="wishlist-input" value={wishlistUrl} onChange={(e) => setWishlistUrl(e.target.value)} placeholder="https://www.amazon.com/hz/wishlist/ls/..." className="w-full brutal-2 p-2 font-display text-sm focus:outline-none bg-white" />
          <button data-testid="wishlist-save-btn" onClick={saveWishlist} className="bg-black text-white font-display font-black uppercase text-xs px-4 py-2 shrink-0">Save</button>
        </div>
      </div>

      {/* Bluevine payment link */}
      <div className="brutal p-4 mb-6" data-testid="bluevine-panel">
        <h2 className="font-display text-xl font-black uppercase tracking-tight mb-1">Online Payment Link (Bluevine)</h2>
        <p className="font-display text-xs text-gray-500 mb-2">Where competitors & donors are sent to pay online. They enter the amount themselves; you confirm each payment in the registrations list ("Mark Paid") once it lands.</p>
        <div className="flex items-center gap-2">
          <input data-testid="bluevine-input" value={bluevineUrl} onChange={(e) => setBluevineUrl(e.target.value)} placeholder="https://pay.bluevine.com/p/..." className="w-full brutal-2 p-2 font-display text-sm focus:outline-none bg-white" />
          <button data-testid="bluevine-save-btn" onClick={saveBluevine} className="bg-black text-white font-display font-black uppercase text-xs px-4 py-2 shrink-0">Save</button>
        </div>
      </div>

      {/* Accounts */}
      <div className="brutal p-4 mb-6" data-testid="accounts-panel">
        <h2 className="font-display text-xl font-black uppercase tracking-tight mb-3">Accounts</h2>
        <div className="space-y-2 mb-4" data-testid="users-list">
          {users.map((u) => (
            <div key={u.id} className="brutal-2 p-2 flex items-center justify-between gap-2" data-testid={`user-${u.id}`}>
              <div className="min-w-0">
                <div className="font-display font-black uppercase truncate">{u.name || u.email}</div>
                <div className="font-display text-[11px] text-gray-500 truncate">{u.email}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-[9px] font-black uppercase px-1.5 py-0.5 ${u.role === "admin" ? "bg-black text-white" : "bg-[#FFD700] text-black"}`}>
                  {u.role === "admin" ? "Admin" : `Judge ${u.judge_num}`}
                </span>
                {u.id !== me.id && (
                  <button data-testid={`delete-user-${u.id}`} onClick={() => removeUser(u.id)} className="text-[#E63946]">
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="brutal-2 p-3">
          <h3 className="font-display font-black uppercase text-sm mb-2">Add Account</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
            <input data-testid="new-user-name" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} placeholder="Name" className="brutal-2 p-2 font-display focus:outline-none bg-white" />
            <input data-testid="new-user-email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} placeholder="Email" className="brutal-2 p-2 font-display focus:outline-none bg-white" />
            <input data-testid="new-user-password" type="text" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} placeholder="Password (min 6)" className="brutal-2 p-2 font-display focus:outline-none bg-white" />
            <select data-testid="new-user-role" value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })} className="brutal-2 p-2 font-display focus:outline-none bg-white">
              <option value="judge">Judge</option>
              <option value="admin">Admin</option>
            </select>
            {newUser.role === "judge" && (
              <select data-testid="new-user-judge-num" value={newUser.judge_num} onChange={(e) => setNewUser({ ...newUser, judge_num: Number(e.target.value) })} className="brutal-2 p-2 font-display focus:outline-none bg-white">
                <option value={1}>Judge seat 1</option>
                <option value={2}>Judge seat 2</option>
                <option value={3}>Judge seat 3</option>
              </select>
            )}
          </div>
          <button data-testid="create-user-btn" onClick={createUser} className="w-full bg-black text-white font-display font-black uppercase tracking-widest py-2 text-sm active:scale-95 transition-transform">
            Create Account
          </button>
        </div>
      </div>

      {/* My password */}
      <div className="brutal p-4 mb-6" data-testid="password-panel">
        <h2 className="font-display text-xl font-black uppercase tracking-tight mb-3">Change My Password</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-end">
          <div>
            <label className="font-display text-[10px] uppercase tracking-widest text-gray-500">Current</label>
            <input data-testid="pw-current" type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} className="w-full brutal-2 mt-1 p-2 font-display focus:outline-none bg-white" />
          </div>
          <div>
            <label className="font-display text-[10px] uppercase tracking-widest text-gray-500">New</label>
            <input data-testid="pw-new" type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} className="w-full brutal-2 mt-1 p-2 font-display focus:outline-none bg-white" />
          </div>
          <button data-testid="pw-change-btn" onClick={changePassword} className="bg-black text-white font-display font-black uppercase py-2 text-xs">
            Update
          </button>
        </div>
      </div>

      {/* Volunteers */}
      <div className="brutal p-4 mb-6" data-testid="volunteers-panel">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
          <h2 className="font-display text-xl font-black uppercase tracking-tight">
            Volunteers ({volunteers.length})
            {volunteers.filter((v) => v.status !== "confirmed").length > 0 && (
              <span className="ml-2 bg-[#E63946] text-white text-[10px] font-black uppercase px-2 py-1 align-middle" data-testid="volunteers-pending-count">
                {volunteers.filter((v) => v.status !== "confirmed").length} pending
              </span>
            )}
          </h2>
          <button data-testid="export-volunteers-btn" onClick={exportVolunteers} className="brutal-2 font-display font-bold uppercase text-xs px-3 py-2">
            Export CSV
          </button>
        </div>
        {volunteers.length === 0 ? (
          <p className="font-display text-gray-400 uppercase text-xs tracking-widest">No volunteers yet</p>
        ) : (
          <div className="space-y-2" data-testid="volunteers-list">
            {volunteers.map((v) => (
              <div key={v.id} className="brutal-2 p-2 flex items-start justify-between gap-2" data-testid={`volunteer-${v.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-display font-black uppercase truncate">{v.name}</span>
                    {v.status === "confirmed"
                      ? <span className="bg-green-600 text-white text-[9px] font-black uppercase px-1.5 py-0.5" data-testid={`vol-status-${v.id}`}>Confirmed</span>
                      : <span className="bg-[#FFB800] text-black text-[9px] font-black uppercase px-1.5 py-0.5" data-testid={`vol-status-${v.id}`}>Pending</span>}
                  </div>
                  <div className="font-display text-[11px] text-gray-500 truncate">{v.email} · {v.phone}</div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {v.shirt_size
                      ? <span className="bg-black text-white text-[9px] font-bold uppercase px-1.5 py-0.5" data-testid={`vol-shirt-${v.id}`}>👕 {v.shirt_size}</span>
                      : <span className="bg-[#E63946] text-white text-[9px] font-bold uppercase px-1.5 py-0.5" data-testid={`vol-shirt-${v.id}`}>No size</span>}
                    {(v.shift_labels || []).map((s) => (
                      <span key={s} className="bg-[#FFD700] text-black text-[9px] font-bold uppercase px-1.5 py-0.5">{s}</span>
                    ))}
                  </div>
                  {v.notes && <div className="font-display text-[11px] text-gray-500 mt-1">"{v.notes}"</div>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {v.status !== "confirmed" && (
                    <button data-testid={`approve-volunteer-${v.id}`} onClick={() => approveVolunteer(v.id)} className="bg-green-600 text-white font-display font-bold uppercase text-[10px] px-2 py-1">
                      Approve
                    </button>
                  )}
                  <button data-testid={`delete-volunteer-${v.id}`} onClick={() => removeVolunteer(v.id)} className="text-[#E63946]">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bucket Garden Workshop */}
      <div className="brutal p-4 mb-6" data-testid="workshop-panel">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
          <h2 className="font-display text-xl font-black uppercase tracking-tight">
            Bucket Garden Workshop
            {workshopInfo && (
              <span className="ml-2 bg-green-600 text-white text-[10px] font-black uppercase px-2 py-1 align-middle" data-testid="workshop-count-badge">
                {workshopInfo.gardens_claimed}/{workshopInfo.capacity} gardens
              </span>
            )}
          </h2>
          <button data-testid="export-workshop-btn" onClick={exportWorkshop} className="brutal-2 font-display font-bold uppercase text-xs px-3 py-2">
            Export CSV
          </button>
        </div>

        {workshopInfo && (
          <div className="grid grid-cols-3 gap-2 mb-3">
            <div className="brutal-2 p-2 text-center">
              <div className="font-display text-[9px] uppercase tracking-widest text-gray-500">Reserved</div>
              <div className="font-score text-2xl" data-testid="workshop-reserved">{workshopInfo.gardens_claimed}</div>
            </div>
            <div className="brutal-2 p-2 text-center">
              <div className="font-display text-[9px] uppercase tracking-widest text-gray-500">Spots Left</div>
              <div className={`font-score text-2xl ${workshopInfo.full ? "text-[#E63946]" : "text-green-700"}`} data-testid="workshop-left">{workshopInfo.spots_left}</div>
            </div>
            <div className="brutal-2 p-2 text-center">
              <div className="font-display text-[9px] uppercase tracking-widest text-gray-500">People Coming</div>
              <div className="font-score text-2xl" data-testid="workshop-people">{workshopInfo.party_total}</div>
            </div>
          </div>
        )}

        <div className="brutal-2 p-3 mb-3 flex items-center justify-between gap-2">
          <span className="font-display font-black uppercase text-sm">Garden Capacity</span>
          <div className="flex items-center gap-1">
            <input data-testid="workshop-cap-input" type="number" min="0" step="1" value={workshopCapInput} onChange={(e) => setWorkshopCapInput(e.target.value)} placeholder={workshopInfo ? `${workshopInfo.capacity}` : "40"} className="brutal-2 p-1 w-20 font-display text-sm focus:outline-none bg-white" />
            <button data-testid="workshop-cap-save" onClick={saveWorkshopCap} className="bg-black text-white font-display font-bold uppercase text-[10px] px-2 py-1">Save</button>
          </div>
        </div>

        {workshopSignups.length === 0 ? (
          <p className="font-display text-gray-400 uppercase text-xs tracking-widest">No garden reservations yet — share the Garden page</p>
        ) : (
          <div className="space-y-2" data-testid="workshop-list">
            {workshopSignups.map((s) => (
              <div key={s.id} className="brutal-2 p-2 flex items-start justify-between gap-2" data-testid={`workshop-signup-${s.id}`}>
                <div className="min-w-0">
                  <div className="font-display font-black uppercase truncate">
                    {s.name}
                    <span className="ml-2 bg-green-600 text-white text-[9px] font-bold uppercase px-1.5 py-0.5">{s.party_size} coming</span>
                  </div>
                  <div className="font-display text-[11px] text-gray-500 truncate">{s.email}{s.phone ? ` · ${s.phone}` : ""}</div>
                  {s.note && <div className="font-display text-[11px] text-gray-500 mt-1">"{s.note}"</div>}
                </div>
                <button data-testid={`delete-workshop-${s.id}`} onClick={() => removeWorkshopSignup(s.id)} className="text-[#E63946] shrink-0">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Score Editor */}
      <ScoreEditor />

      {/* Check-In */}
      <div className="brutal p-4 mb-6" data-testid="checkin-panel">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-xl font-black uppercase tracking-tight">Check-In</h2>
          <button data-testid="checkin-refresh-btn" onClick={loadCheckin} className="brutal-2 font-display font-bold uppercase text-xs px-3 py-2">Refresh</button>
        </div>
        <p className="font-display text-xs text-gray-500 mb-3">Mark who showed up. No-shows free a spot you can fill from the waitlist below.</p>
        {!checkinData || checkinData.divisions.every((d) => d.total === 0) ? (
          <p className="font-display text-sm text-gray-500" data-testid="checkin-empty">No competitors placed yet.</p>
        ) : (
          <div className="space-y-4">
            {checkinData.divisions.filter((d) => d.total > 0).map((d) => (
              <div key={d.id} className="brutal-2 p-3" data-testid={`checkin-div-${d.id}`}>
                <div className="flex items-center justify-between flex-wrap gap-1 mb-2">
                  <span className="font-display font-black uppercase tracking-tight">{d.name}</span>
                  <span className="font-display text-[11px] uppercase tracking-widest text-gray-600" data-testid={`checkin-summary-${d.id}`}>
                    {d.checked_in}/{d.total} in
                    {d.no_shows > 0 && <span className="text-[#E63946]"> · {d.no_shows} no-show</span>}
                    {d.spots_open > 0 && <span className="text-[#B45309]"> · {d.spots_open} spot{d.spots_open === 1 ? "" : "s"} open</span>}
                    {d.waitlist_count > 0 && <span className="text-gray-500"> · {d.waitlist_count} waiting</span>}
                  </span>
                </div>
                <div className="space-y-1">
                  {d.riders.map((r) => (
                    <div key={r.id} className="flex items-center gap-2 flex-wrap" data-testid={`checkin-rider-${r.id}`}>
                      <span className="font-display font-bold uppercase text-sm flex-1 min-w-0 truncate">
                        {r.name}
                        {!r.paid && <span className="ml-2 bg-[#FFB800] text-black text-[9px] font-black uppercase px-1.5 py-0.5">Unpaid</span>}
                      </span>
                      <button
                        data-testid={`checkin-in-${r.id}`}
                        onClick={() => toggleCheckin(r.id, r.checkin, "in")}
                        className={`brutal-2 font-display font-black uppercase text-[10px] px-3 py-1 ${r.checkin === "in" ? "bg-green-600 text-white" : "bg-white"}`}
                      >✓ In</button>
                      <button
                        data-testid={`checkin-noshow-${r.id}`}
                        onClick={() => toggleCheckin(r.id, r.checkin, "no_show")}
                        className={`brutal-2 font-display font-black uppercase text-[10px] px-3 py-1 ${r.checkin === "no_show" ? "bg-[#E63946] text-white" : "bg-white"}`}
                      >✗ No-Show</button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Waitlist */}
      <div className="brutal p-4 mb-6" data-testid="waitlist-panel">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-xl font-black uppercase tracking-tight">Waitlist</h2>
          <button data-testid="waitlist-refresh-btn" onClick={loadWaitlist} className="brutal-2 font-display font-bold uppercase text-xs px-3 py-2">Refresh</button>
        </div>
        <p className="font-display text-xs text-gray-500 mb-3">People waiting for a full division. Promote someone when a spot opens — they're placed immediately and marked unpaid (settle in person).</p>
        {!waitlistData || waitlistData.divisions.length === 0 ? (
          <p className="font-display text-sm text-gray-500" data-testid="waitlist-empty">No one on the waitlist right now.</p>
        ) : (
          <div className="space-y-4">
            {waitlistData.divisions.map((d) => (
              <div key={d.id} className="brutal-2 p-3" data-testid={`waitlist-div-${d.id}`}>
                <div className="flex items-center justify-between flex-wrap gap-1 mb-2">
                  <span className="font-display font-black uppercase tracking-tight">{d.name}</span>
                  <span className="font-display text-[11px] uppercase tracking-widest text-gray-600">
                    {d.entries.length} waiting{d.spots_left != null ? ` · ${d.spots_left} spot${d.spots_left === 1 ? "" : "s"} open` : ""}
                  </span>
                </div>
                <div className="space-y-1">
                  {d.entries.map((e, i) => (
                    <div key={e.reg_id} className="flex items-center gap-2 flex-wrap" data-testid={`waitlist-entry-${e.reg_id}`}>
                      <span className="font-score text-lg w-6 text-center text-gray-400">{i + 1}</span>
                      <span className="font-display font-bold uppercase text-sm flex-1 min-w-0 truncate">
                        {e.name} <span className="text-gray-500 font-normal normal-case">· age {e.age} · {e.phone}</span>
                      </span>
                      <button
                        data-testid={`waitlist-promote-${e.reg_id}`}
                        onClick={() => promote(e.reg_id, d.id, e.name, d.name)}
                        className="brutal-2 bg-green-600 text-white font-display font-black uppercase text-[10px] px-3 py-1 active:scale-95 transition-transform"
                      >▲ Promote</button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Registrations */}
      <div className="brutal p-4 mb-6" data-testid="registrations-panel">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-xl font-black uppercase tracking-tight">Registrations</h2>
          <div className="flex items-center gap-2">
            <button data-testid="export-registrations-btn" onClick={exportRegistrations} className="brutal-2 font-display font-bold uppercase text-xs px-3 py-2">
              Export CSV
            </button>
            <span className="brutal-2 bg-black text-white font-score text-2xl px-3" data-testid="registrations-count">{regs.length}</span>
          </div>
        </div>
        {(() => {
          const filters = [
            { id: "all", label: "All", test: () => true },
            { id: "paid", label: "Paid", test: (r) => r.payment_status === "paid" },
            { id: "cash_pending", label: "Cash · Holding", test: (r) => r.payment_status === "cash_pending" },
            { id: "bluevine_pending", label: "Online · Holding", test: (r) => r.payment_status === "bluevine_pending" },
            { id: "claimed", label: "💸 Says Paid · Confirm", test: (r) => r.payment_status === "bluevine_pending" && r.payment_claimed },
            { id: "sponsor_pending", label: "Sponsor · Requested", test: (r) => r.payment_status === "sponsor_pending" },
            { id: "waitlist", label: "Waitlist", test: (r) => r.payment_status === "waitlist" },
            { id: "unpaid", label: "Unpaid", test: (r) => !["paid", "cash_pending", "bluevine_pending", "sponsor_pending", "waitlist"].includes(r.payment_status) },
            { id: "needs_sponsor", label: "Needs Sponsor", test: (r) => r.needs_sponsorship && !(r.sponsorship_applied > 0) },
            { id: "no_shirt", label: "Missing Shirt", test: (r) => !r.shirt_size },
          ];
          const active = filters.find((f) => f.id === regFilter) || filters[0];
          const filtered = regs.filter(active.test);
          return (
            <>
              <div className="flex flex-wrap gap-1.5 mb-3" data-testid="registrations-filters">
                {filters.map((f) => {
                  const count = regs.filter(f.test).length;
                  const isActive = regFilter === f.id;
                  return (
                    <button
                      key={f.id}
                      data-testid={`reg-filter-${f.id}`}
                      onClick={() => setRegFilter(f.id)}
                      className={`font-display font-bold uppercase text-[10px] tracking-wide px-2.5 py-1.5 border-2 border-black transition-colors ${isActive ? "bg-black text-white" : "bg-white text-black"}`}
                    >
                      {f.label} <span className={isActive ? "text-[#FFD700]" : "text-gray-400"}>{count}</span>
                    </button>
                  );
                })}
              </div>
              <button
                data-testid="build-all-heats-btn"
                onClick={buildAll}
                className="w-full brutal bg-[#FFD700] font-display font-black uppercase tracking-widest py-3 mb-3 active:scale-[0.99] transition-transform"
              >
                ⚡ Build Heats From All Registrations
              </button>
              <p className="font-display text-[11px] text-gray-500 mb-3">
                Places every confirmed competitor into their division heats. Skips any division you've already started scoring.
              </p>
              {regs.length === 0 ? (
                <p className="font-display text-gray-400 uppercase text-xs tracking-widest">No registrations yet — share the Enter page</p>
              ) : filtered.length === 0 ? (
                <p className="font-display text-gray-400 uppercase text-xs tracking-widest" data-testid="registrations-empty-filter">No registrations match "{active.label}"</p>
              ) : (
                <div className="space-y-2" data-testid="registrations-list">
                  {filtered.map((r) => (
              <div key={r.id} className="brutal-2 p-3" data-testid={`registration-${r.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <button className="text-left flex-1" onClick={() => setOpenReg(openReg === r.id ? null : r.id)}>
                    <div className="font-display font-black uppercase flex items-center gap-2 flex-wrap">
                      {r.full_name}
                      <span className="font-score text-lg text-gray-500">age {r.age}</span>
                      {r.is_minor && <span className="bg-[#E63946] text-white text-[9px] font-black uppercase px-1.5 py-0.5">Minor</span>}
                      {r.payment_status === "paid" && <span className="bg-black text-white text-[9px] font-black uppercase px-1.5 py-0.5">Paid</span>}
                      {r.payment_status === "cash_pending" && <span className="bg-[#FFB800] text-black text-[9px] font-black uppercase px-1.5 py-0.5">Cash · Holding</span>}
                      {r.payment_status === "bluevine_pending" && (r.payment_claimed
                        ? <span className="bg-green-600 text-white text-[9px] font-black uppercase px-1.5 py-0.5" data-testid={`claimed-${r.id}`}>💸 Says Paid · Confirm</span>
                        : <span className="bg-[#FF7A00] text-white text-[9px] font-black uppercase px-1.5 py-0.5">Online · Holding</span>)}
                      {r.payment_status === "sponsor_pending" && <span className="bg-[#E63946] text-white text-[9px] font-black uppercase px-1.5 py-0.5">Sponsor · Requested</span>}
                      {r.payment_status === "waitlist" && <span className="bg-[#FFB800] text-black text-[9px] font-black uppercase px-1.5 py-0.5" data-testid={`waitlist-badge-${r.id}`}>Waitlist</span>}
                      {!["paid", "cash_pending", "bluevine_pending", "sponsor_pending", "waitlist"].includes(r.payment_status) && <span className="brutal-2 text-[9px] font-black uppercase px-1.5 py-0.5 text-gray-500">{r.placed_unpaid ? "Unpaid · Placed" : "Unpaid"}</span>}
                      {r.sponsorship_applied > 0 && <span className="bg-green-600 text-white text-[9px] font-black uppercase px-1.5 py-0.5">Sponsored -${Number(r.sponsorship_applied).toFixed(0)}</span>}
                      {r.needs_sponsorship && !(r.sponsorship_applied > 0) && <span className="bg-[#FFD700] text-black text-[9px] font-black uppercase px-1.5 py-0.5">Needs Sponsor</span>}
                    </div>
                    <div className="font-display text-[11px] uppercase tracking-widest text-gray-500">{r.division_names.join(" · ")}</div>
                  </button>
                  <div className="flex items-center gap-2">
                    {r.age <= 17 && !(r.sponsorship_applied > 0) && (
                      <button data-testid={`sponsor-registration-${r.id}`} onClick={() => sponsorReg(r.id)} className="bg-[#FFD700] text-black font-display font-bold uppercase text-[10px] px-2 py-1">
                        {r.payment_status === "sponsor_pending" ? "Approve Sponsor" : "Sponsor"}
                      </button>
                    )}
                    {r.payment_status !== "paid" && (
                      <button data-testid={`approve-registration-${r.id}`} onClick={() => approveReg(r.id)} className="bg-green-600 text-white font-display font-bold uppercase text-[10px] px-2 py-1">
                        Mark Paid
                      </button>
                    )}
                    <button data-testid={`delete-registration-${r.id}`} onClick={() => deleteReg(r.id)} className="text-[#E63946]">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                {openReg === r.id && (
                  <div className="mt-3 pt-3 border-t-2 border-black grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 font-display text-sm">
                    <div><span className="text-gray-500 uppercase text-[10px] tracking-widest">DOB</span> {r.dob}</div>
                    <div><span className="text-gray-500 uppercase text-[10px] tracking-widest">Gender</span> {r.gender}</div>
                    <div><span className="text-gray-500 uppercase text-[10px] tracking-widest">Email</span> {r.email}</div>
                    <div><span className="text-gray-500 uppercase text-[10px] tracking-widest">Phone</span> {r.phone}</div>
                    <div><span className="text-gray-500 uppercase text-[10px] tracking-widest">Shirt</span> {r.shirt_size || "—"}</div>
                    <div><span className="text-gray-500 uppercase text-[10px] tracking-widest">Emergency</span> {r.emergency_contact_name} · {r.emergency_contact_phone}</div>
                    <div><span className="text-gray-500 uppercase text-[10px] tracking-widest">Signed</span> {r.signature}</div>
                    {r.sponsorship_reason && (
                      <div className="sm:col-span-2 mt-1 bg-[#FFF7D6] brutal-2 p-2" data-testid={`sponsorship-reason-${r.id}`}>
                        <span className="text-gray-500 uppercase text-[10px] tracking-widest block mb-0.5">Their story — why they're applying</span> {r.sponsorship_reason}
                      </div>
                    )}
                    {r.is_minor && (
                      <div className="sm:col-span-2 mt-1 bg-gray-50 brutal-2 p-2">
                        <span className="text-gray-500 uppercase text-[10px] tracking-widest">Guardian</span> {r.guardian_name} ({r.guardian_relationship}) · {r.guardian_phone} · signed: {r.guardian_signature} · consent: {r.guardian_consent ? "Yes" : "No"}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
                </div>
              )}
            </>
          );
        })()}
      </div>

      {/* Finances */}
      {finance && (
      <div className="brutal p-4 mb-6" data-testid="finance-panel">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
          <h2 className="font-display text-xl font-black uppercase tracking-tight">Finances</h2>
          <button data-testid="export-finance-btn" onClick={exportFinance} className="brutal-2 font-display font-bold uppercase text-xs px-3 py-2">Export CSV</button>
        </div>
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="brutal-2 p-2 text-center">
            <div className="font-display text-[9px] uppercase tracking-widest text-gray-500">Income</div>
            <div className="font-score text-2xl text-green-700" data-testid="finance-income">${finance.total_income.toFixed(0)}</div>
          </div>
          <div className="brutal-2 p-2 text-center">
            <div className="font-display text-[9px] uppercase tracking-widest text-gray-500">Expenses</div>
            <div className="font-score text-2xl text-[#E63946]" data-testid="finance-expenses">${finance.total_expenses.toFixed(0)}</div>
          </div>
          <div className={`brutal-2 p-2 text-center ${finance.net < 0 ? "bg-[#E63946] text-white" : "bg-green-600 text-white"}`}>
            <div className="font-display text-[9px] uppercase tracking-widest opacity-80">Net</div>
            <div className="font-score text-2xl" data-testid="finance-net">${finance.net.toFixed(0)}</div>
          </div>
        </div>
        <div className="brutal-2 p-2 mb-3 font-display text-[11px] text-gray-600">
          <div className="uppercase tracking-widest text-gray-400 text-[9px] mb-1">Auto income (from the app)</div>
          <div className="flex justify-between"><span>Entry fees collected</span><span className="font-bold">${finance.entries_income.toFixed(2)}</span></div>
          <div className="flex justify-between"><span>Sponsor donations</span><span className="font-bold">${finance.donations_income.toFixed(2)}</span></div>
        </div>

        {finance.expected_income > 0 && (
          <div className="brutal-2 bg-[#FFF7D6] p-2 mb-3 font-display text-[11px]" data-testid="finance-expected">
            <div className="flex justify-between">
              <span className="uppercase tracking-widest text-gray-500 text-[9px]">Still coming in (cash + online holding)</span>
              <span className="font-score text-base text-[#B8860B]">+${finance.expected_income.toFixed(0)}</span>
            </div>
            <div className="flex justify-between mt-1">
              <span className="uppercase tracking-widest text-gray-500 text-[9px]">Projected net once collected</span>
              <span className="font-score text-base">${(finance.net + finance.expected_income).toFixed(0)}</span>
            </div>
          </div>
        )}

        {/* Budget target */}
        <div className="brutal-2 p-3 mb-3" data-testid="finance-budget">
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="font-display font-black uppercase text-sm">Expense Budget</span>
            <div className="flex items-center gap-1">
              <input data-testid="budget-input" type="number" min="0" step="1" value={budgetInput} onChange={(e) => setBudgetInput(e.target.value)} placeholder={finance.budget ? `$${finance.budget.toFixed(0)}` : "Set $"} className="brutal-2 p-1 w-24 font-display text-sm focus:outline-none bg-white" />
              <button data-testid="budget-save-btn" onClick={saveBudget} className="bg-black text-white font-display font-bold uppercase text-[10px] px-2 py-1">Save</button>
            </div>
          </div>
          {finance.budget > 0 && (() => {
            const pct = Math.min(100, Math.round((finance.total_expenses / finance.budget) * 100));
            const over = finance.total_expenses > finance.budget;
            const warn = pct >= 80;
            return (
              <>
                <div className="brutal-2 h-4 w-full bg-white overflow-hidden">
                  <div className={`h-full transition-[width] duration-500 ${over ? "bg-[#E63946]" : warn ? "bg-[#FFB800]" : "bg-green-600"}`} style={{ width: `${pct}%` }} data-testid="budget-bar" />
                </div>
                <p className={`font-display text-[11px] mt-1 ${over ? "text-[#E63946] font-bold" : "text-gray-500"}`} data-testid="budget-status">
                  ${finance.total_expenses.toFixed(0)} of ${finance.budget.toFixed(0)} spent ({pct}%){over ? " — over budget!" : warn ? " — getting close" : ""}
                </p>
              </>
            );
          })()}
        </div>

        {/* Expense breakdown */}
        {Object.keys(finance.expense_by_category || {}).length > 0 && (
          <div className="brutal-2 p-3 mb-3" data-testid="expense-breakdown">
            <div className="font-display font-black uppercase text-sm mb-2">Where It's Going</div>
            {Object.entries(finance.expense_by_category).sort((a, b) => b[1] - a[1]).map(([cat, amt]) => {
              const pct = finance.total_expenses > 0 ? Math.round((amt / finance.total_expenses) * 100) : 0;
              return (
                <div key={cat} className="mb-2" data-testid={`breakdown-${cat}`}>
                  <div className="flex justify-between font-display text-[11px] mb-0.5">
                    <span className="uppercase font-bold">{cat}</span>
                    <span>${amt.toFixed(0)} · {pct}%</span>
                  </div>
                  <div className="brutal-2 h-3 w-full bg-white overflow-hidden">
                    <div className="h-full bg-[#E63946]" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Add entry */}
        <div className="brutal-2 p-3 mb-3">
          <div className="flex gap-2 mb-2">
            {["expense", "income"].map((k) => (
              <button key={k} data-testid={`fin-kind-${k}`} onClick={() => setNewFin({ ...newFin, kind: k, category: k === "expense" ? "Shirts" : "Gate Cash" })}
                className={`flex-1 font-display font-bold uppercase text-xs py-2 ${newFin.kind === k ? (k === "expense" ? "bg-[#E63946] text-white" : "bg-green-600 text-white") : "brutal-2"}`}>
                {k}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
            <input data-testid="fin-description" value={newFin.description} onChange={(e) => setNewFin({ ...newFin, description: e.target.value })} placeholder="Description" className="brutal-2 p-2 font-display focus:outline-none bg-white" />
            <input data-testid="fin-amount" type="number" min="0" step="0.01" value={newFin.amount} onChange={(e) => setNewFin({ ...newFin, amount: e.target.value })} placeholder="Amount $" className="brutal-2 p-2 font-display focus:outline-none bg-white" />
            <select data-testid="fin-category" value={newFin.category} onChange={(e) => setNewFin({ ...newFin, category: e.target.value })} className="brutal-2 p-2 font-display focus:outline-none bg-white">
              {(newFin.kind === "expense" ? EXPENSE_CATS : INCOME_CATS).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input data-testid="fin-date" type="date" value={newFin.date} onChange={(e) => setNewFin({ ...newFin, date: e.target.value })} className="brutal-2 p-2 font-display focus:outline-none bg-white" />
          </div>
          <input data-testid="fin-note" value={newFin.note} onChange={(e) => setNewFin({ ...newFin, note: e.target.value })} placeholder="Note (optional)" className="w-full brutal-2 p-2 font-display focus:outline-none bg-white mb-2" />
          <button data-testid="fin-add-btn" onClick={addFinance} className="w-full bg-black text-white font-display font-black uppercase tracking-widest py-2 text-sm">Add {newFin.kind}</button>
        </div>

        {/* Ledger */}
        {finance.entries.length > 0 && (
          <div className="space-y-1" data-testid="finance-ledger">
            {finance.entries.map((e) => (
              <div key={e.id} className="brutal-2 p-2 flex items-center justify-between gap-2" data-testid={`finance-entry-${e.id}`}>
                <div className="min-w-0">
                  <div className="font-display font-bold uppercase text-sm truncate">{e.description}</div>
                  <div className="font-display text-[10px] uppercase tracking-widest text-gray-400">{e.date} · {e.category}{e.note ? ` · ${e.note}` : ""}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`font-score text-lg ${e.kind === "expense" ? "text-[#E63946]" : "text-green-700"}`}>{e.kind === "expense" ? "-" : "+"}${e.amount.toFixed(2)}</span>
                  <button data-testid={`delete-finance-${e.id}`} onClick={() => removeFinance(e.id)} className="text-[#E63946]"><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {/* Shirt Orders */}
      <div className="brutal p-4 mb-6" data-testid="shirt-orders-panel">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
          <h2 className="font-display text-xl font-black uppercase tracking-tight">Shirt Orders</h2>
          <div className="flex items-center gap-2">
            <button data-testid="shirt-remind-btn" onClick={sendShirtReminders} className="brutal-2 font-display font-bold uppercase text-xs px-3 py-2 bg-[#E63946] text-white">
              Email Missing Sizes
            </button>
            <button data-testid="export-shirts-btn" onClick={exportShirts} className="brutal-2 font-display font-bold uppercase text-xs px-3 py-2">
              Export CSV
            </button>
            <button data-testid="export-shirts-role-btn" onClick={exportShirtsByRole} className="brutal-2 font-display font-bold uppercase text-xs px-3 py-2">
              By Role/Color
            </button>
          </div>
        </div>
        <p className="font-display text-[11px] text-gray-500 mb-3">Participants and volunteers are split so you can order different colors for each.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="shirt-table">
            <thead>
              <tr className="font-display uppercase text-[10px] tracking-widest text-gray-500 text-left border-b-2 border-black">
                <th className="py-1">Size</th>
                <th className="py-1 text-center">Participants</th>
                <th className="py-1 text-center">Volunteers</th>
                <th className="py-1 text-center">Total</th>
              </tr>
            </thead>
            <tbody>
              {SHIRT_SIZES.map((sz) => {
                const p = regs.filter((r) => r.shirt_size === sz).length;
                const v = volunteers.filter((x) => x.shirt_size === sz).length;
                if (p + v === 0) return null;
                return (
                  <tr key={sz} className="font-display border-b border-gray-200" data-testid={`shirt-row-${sz}`}>
                    <td className="py-1 font-bold uppercase">{sz}</td>
                    <td className="py-1 text-center font-score">{p}</td>
                    <td className="py-1 text-center font-score">{v}</td>
                    <td className="py-1 text-center font-score">{p + v}</td>
                  </tr>
                );
              })}
              <tr className="font-display border-t-2 border-black font-black uppercase">
                <td className="py-1">Total</td>
                <td className="py-1 text-center font-score">{regs.filter((r) => r.shirt_size).length}</td>
                <td className="py-1 text-center font-score">{volunteers.filter((v) => v.shirt_size).length}</td>
                <td className="py-1 text-center font-score">{regs.filter((r) => r.shirt_size).length + volunteers.filter((v) => v.shirt_size).length}</td>
              </tr>
            </tbody>
          </table>
        </div>
        {(regs.filter((r) => !r.shirt_size).length + volunteers.filter((v) => !v.shirt_size).length) > 0 ? (
          <div className="mt-3" data-testid="shirt-outstanding">
            <p className="font-display text-xs text-[#E63946] font-bold mb-2">
              Still outstanding — {regs.filter((r) => !r.shirt_size).length} participant(s), {volunteers.filter((v) => !v.shirt_size).length} volunteer(s):
            </p>
            <div className="flex flex-wrap gap-1">
              {regs.filter((r) => !r.shirt_size).map((r) => (
                <span key={r.id} className="brutal-2 text-[10px] font-bold uppercase px-1.5 py-0.5">{r.full_name} <span className="text-gray-400">· comp</span></span>
              ))}
              {volunteers.filter((v) => !v.shirt_size).map((v) => (
                <span key={v.id} className="brutal-2 text-[10px] font-bold uppercase px-1.5 py-0.5">{v.name} <span className="text-gray-400">· vol</span></span>
              ))}
            </div>
          </div>
        ) : (
          <p className="font-display text-xs text-green-700 font-bold mt-3" data-testid="shirt-all-in">✅ Everyone has a shirt size — ready to order!</p>
        )}
      </div>

      {/* Donor History */}
      <div className="brutal p-4 mb-6" data-testid="donor-history-panel">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-display text-xl font-black uppercase tracking-tight">Sponsorship Fund</h2>
            {fund && (
              <p className="font-display text-xs text-gray-500">
                <span className="text-green-700 font-black">${Number(fund.available).toFixed(0)} available</span>
                {" · "}${Number(fund.total_donated).toFixed(0)} donated · ${Number(fund.total_allocated).toFixed(0)} used
              </p>
            )}
          </div>
          <button
            data-testid="toggle-donors-btn"
            onClick={() => setShowDonors((s) => !s)}
            className="brutal-2 font-display font-bold uppercase text-xs px-4 py-2"
          >
            {showDonors ? "Hide Donors" : `Donors (${donations.length})`}
          </button>
        </div>
        {showDonors && (
          <div className="mt-3 pt-3 border-t-2 border-black space-y-2" data-testid="donor-list">
            <div className="brutal-2 bg-[#FFF7D6] p-3" data-testid="record-donation">
              <p className="font-display text-xs font-bold uppercase tracking-wide mb-2">Record a Bluevine / cash donation → adds to the fund</p>
              <div className="flex items-center gap-2">
                <input data-testid="donor-name-input" value={donorName} onChange={(e) => setDonorName(e.target.value)} placeholder="Donor name (optional)" className="flex-1 min-w-0 brutal-2 p-2 font-display text-sm focus:outline-none bg-white" />
                <input data-testid="donor-amount-input" type="number" min="1" step="1" value={donorAmount} onChange={(e) => setDonorAmount(e.target.value)} placeholder="$" className="w-20 brutal-2 p-2 font-display text-sm focus:outline-none bg-white" />
                <button data-testid="record-donation-btn" onClick={recordDonation} className="bg-green-600 text-white font-display font-black uppercase text-xs px-3 py-2 shrink-0">Add</button>
              </div>
            </div>
            {donations.length === 0 ? (
              <p className="font-display text-gray-400 uppercase text-xs tracking-widest">No donations yet</p>
            ) : (
              donations.map((d) => (
                <div key={d.id} className="brutal-2 p-2 flex items-center justify-between gap-2" data-testid={`donation-${d.id}`}>
                  <div className="min-w-0">
                    <div className="font-display font-black uppercase truncate">{d.donor_name}</div>
                    {d.message && <div className="font-display text-[11px] text-gray-500 truncate">"{d.message}"</div>}
                    <div className="font-display text-[10px] uppercase tracking-widest text-gray-400">
                      {d.created_at ? new Date(d.created_at).toLocaleDateString() : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="brutal-2 bg-green-600 text-white font-score text-xl px-2">${Number(d.amount).toFixed(0)}</span>
                    <button data-testid={`delete-donation-${d.id}`} onClick={() => removeDonation(d.id)} className="text-[#E63946]"><Trash2 size={16} /></button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Divisions */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-6" data-testid="division-list">
        {divisions.map((d) => (
          <button
            key={d.id}
            data-testid={`division-${d.id}`}
            onClick={() => openDivision(d.id)}
            className={`brutal p-3 text-left active:scale-95 transition-transform ${selected === d.id ? "bg-black text-white" : "bg-white"}`}
          >
            <div className="font-display font-black uppercase text-sm leading-tight">{d.name}</div>
            <div className={`font-display text-xs mt-1 ${selected === d.id ? "text-gray-300" : "text-gray-500"}`}>
              {d.rider_count}/{d.cap} riders · {d.heat_count} heats
            </div>
          </button>
        ))}
      </div>

      {/* Division detail */}
      {detail && (
        <div className="brutal p-4" data-testid="division-detail">
          <h2 className="font-display text-2xl font-black uppercase tracking-tight mb-3">{detail.name}</h2>

          {/* add rider */}
          <div className="flex flex-wrap gap-2 mb-1">
            <input
              data-testid="rider-name-input"
              value={riderName}
              onChange={(e) => setRiderName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addRider()}
              placeholder="Rider name"
              className="flex-1 min-w-[140px] brutal-2 p-3 font-display focus:outline-none"
            />
            <select
              data-testid="rider-shirt-select"
              value={riderShirt}
              onChange={(e) => setRiderShirt(e.target.value)}
              className={`brutal-2 p-3 font-display focus:outline-none bg-white ${!riderShirt ? "text-gray-400" : ""}`}
            >
              <option value="">Shirt size…</option>
              {SHIRT_SIZES.map((sz) => <option key={sz} value={sz}>{sz}</option>)}
            </select>
            <button data-testid="add-rider-btn" onClick={addRider} className="bg-black text-white font-display font-black uppercase px-5">
              Add
            </button>
          </div>
          <p className="font-display text-[11px] text-gray-500 mb-3">Shirt size is required so nobody's shirt gets missed.</p>

          {/* rider list */}
          <div className="grid grid-cols-2 gap-2 mb-4" data-testid="rider-list">
            {detail.riders.map((r) => (
              <div key={r.id} className="brutal-2 flex items-center justify-between p-2">
                <span className="font-display font-bold text-sm truncate">{r.name}</span>
                <button data-testid={`delete-rider-${r.id}`} onClick={async () => { await api.deleteRider(r.id); refresh(); }} className="text-[#E63946]">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
            {detail.riders.length === 0 && <p className="font-display text-gray-400 uppercase text-xs tracking-widest col-span-2">No riders yet</p>}
          </div>

          <div className="brutal-2 p-3 mb-3 flex items-center justify-between gap-2" data-testid="riders-per-heat-control">
            <span className="font-display font-black uppercase text-sm flex items-center gap-1"><Users size={15} /> Riders / Heat</span>
            <div className="flex items-center gap-1">
              <select
                data-testid="riders-per-heat-select"
                value={detail.riders_per_heat || 4}
                onChange={async (e) => { await api.setDivisionSettings(selected, { riders_per_heat: Number(e.target.value) }); flash(`Set to ${e.target.value} riders/heat`); refresh(); }}
                className="brutal-2 p-1.5 font-display font-bold text-sm focus:outline-none bg-white"
              >
                {[2, 3, 4, 5, 6, 7, 8].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          </div>

          <div className="brutal-2 p-3 mb-3 flex items-center justify-between gap-2" data-testid="division-cap-control">
            <div>
              <span className="font-display font-black uppercase text-sm flex items-center gap-1"><ClipboardList size={15} /> Spots (Cap)</span>
              {(() => {
                const dv = divisions.find((x) => x.id === selected);
                const taken = dv ? dv.spots_taken : 0;
                return <span className="font-display text-[11px] text-gray-500">{taken} entered{detail.cap ? ` of ${detail.cap}` : ""}{detail.cap ? ` · ${Math.max(0, detail.cap - taken)} left` : " · no limit"}</span>;
              })()}
            </div>
            <div className="flex items-center gap-1">
              <input
                data-testid="division-cap-input"
                type="number"
                min="0"
                step="1"
                defaultValue={detail.cap || 0}
                key={`cap-${selected}-${detail.cap}`}
                onBlur={async (e) => {
                  const v = Math.max(0, Number(e.target.value) || 0);
                  if (v === (detail.cap || 0)) return;
                  await api.setDivisionSettings(selected, { cap: v });
                  flash(v === 0 ? "Cap removed (unlimited)" : `Cap set to ${v} spots`);
                  refresh();
                }}
                className="brutal-2 p-1.5 w-20 font-display font-bold text-sm focus:outline-none bg-white"
              />
            </div>
          </div>

          <button
            data-testid="generate-heats-btn"
            onClick={async () => { await api.generateHeats(selected); flash("Heats generated"); refresh(); }}
            className="w-full brutal bg-[#FFD700] font-display font-black uppercase tracking-widest py-3 mb-4 active:scale-95 transition-transform"
          >
            Generate Heats ({Math.ceil(detail.riders.length / (detail.riders_per_heat || 4)) || 0}) · {detail.riders_per_heat || 4}/heat
          </button>

          {/* rounds & heats */}
          <div className="space-y-6" data-testid="rounds-list">
            {detail.rounds.map((rnd) => (
              <div key={rnd.id} data-testid={`round-${rnd.id}`}>
                <div className="flex items-center justify-between border-b-4 border-black pb-1 mb-3 flex-wrap gap-2">
                  <h3 className="font-display text-lg font-black uppercase tracking-tight">{rnd.name}</h3>
                  <div className="flex items-center gap-1">
                    <span className="font-display text-[10px] uppercase tracking-widest text-gray-500">Advance top</span>
                    <input
                      data-testid={`advance-count-${rnd.id}`}
                      type="number" min="1" max="4"
                      value={advance[rnd.id] ?? 2}
                      onChange={(e) => setAdvance({ ...advance, [rnd.id]: Number(e.target.value) })}
                      className="brutal-2 w-12 p-1 font-score text-xl text-center focus:outline-none"
                    />
                    <button
                      data-testid={`advance-round-${rnd.id}`}
                      onClick={() => doAdvance(rnd.id)}
                      className="bg-black text-white font-display font-bold uppercase text-xs px-3 py-2 flex items-center gap-1 active:scale-95 transition-transform"
                    >
                      <ChevronsRight size={14} /> Next Round
                    </button>
                  </div>
                </div>
                <div className="space-y-3">
                  {rnd.heats.map((h) => {
                    const isLive = detail.live_heat_id === h.id;
                    const isOpen = h.state === "open";
                    const isOnDeck = detail.on_deck_heat_id === h.id;
                    const isClosed = h.state === "closed";
                    return (
                      <div key={h.id} className="brutal-2 p-3" data-testid={`heat-${h.id}`}>
                        <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
                          <div className="flex items-center gap-2">
                            <span className="font-display font-black uppercase">{h.name}</span>
                            {isOpen && <span className="bg-[#E63946] text-white text-[10px] font-black uppercase px-2 py-0.5" data-testid={`heat-open-badge-${h.id}`}>● Open</span>}
                            {isClosed && <span className="bg-gray-700 text-white text-[10px] font-black uppercase px-2 py-0.5" data-testid={`heat-closed-badge-${h.id}`}>Closed</span>}
                            {isOnDeck && <span className="bg-black text-white text-[10px] font-black uppercase px-2 py-0.5">On Deck</span>}
                            {h.is_final && <span className="bg-[#FFD700] text-black text-[10px] font-black uppercase px-2 py-0.5">Final</span>}
                          </div>
                          <button data-testid={`delete-heat-${h.id}`} onClick={async () => { await api.deleteHeat(h.id); refresh(); }} className="text-[#E63946]">
                            <Trash2 size={16} />
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-2 mb-3">
                          {h.riders.map((r) => (
                            <div key={r.rider_id} className="flex items-center gap-1">
                              <JerseyBadge jersey={r.jersey} size="sm" />
                              <span className="font-display text-sm font-bold truncate max-w-[100px]">{r.name}</span>
                            </div>
                          ))}
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          {isOpen ? (
                            <button data-testid={`close-heat-${h.id}`} onClick={async () => { await api.closeHeat(h.id); flash(`${h.name} closed`); refresh(); }} className="brutal-2 bg-gray-700 text-white font-display font-bold uppercase text-xs py-2 flex items-center justify-center gap-1">
                              <Square size={14} /> Close
                            </button>
                          ) : (
                            <button data-testid={`open-heat-${h.id}`} onClick={async () => { await api.openHeat(h.id); flash(`${h.name} is OPEN`); refresh(); }} className="brutal-2 bg-green-600 text-white font-display font-bold uppercase text-xs py-2 flex items-center justify-center gap-1">
                              <Radio size={14} /> {isClosed ? "Re-open" : "Open"}
                            </button>
                          )}
                          <button data-testid={`on-deck-${h.id}`} onClick={async () => { await api.setOnDeck(h.id); flash(`${h.name} on deck`); refresh(); }} className="brutal-2 font-display font-bold uppercase text-xs py-2 flex items-center justify-center gap-1">
                            <Clock size={14} /> On Deck
                          </button>
                          <button data-testid={`final-${h.id}`} onClick={async () => { await api.setFinal(h.id, !h.is_final); flash("Final toggled"); refresh(); }} className={`brutal-2 font-display font-bold uppercase text-xs py-2 flex items-center justify-center gap-1 ${h.is_final ? "bg-[#FFD700]" : ""}`}>
                            <Flag size={14} /> Final
                          </button>
                        </div>
                        <button
                          data-testid={`reset-heat-${h.id}`}
                          onClick={async () => { if (window.confirm(`Reset ${h.name}? This wipes its scores so the heat can be re-run. Riders stay in the heat.`)) { await api.resetHeat(h.id); flash(`${h.name} reset — ready to redo`); refresh(); } }}
                          className="w-full mt-2 brutal-2 font-display font-bold uppercase text-[11px] py-2 flex items-center justify-center gap-1 text-[#E63946]"
                        >
                          <RotateCcw size={13} /> Reset scores (redo heat)
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            {detail.rounds.length === 0 && <p className="font-display text-gray-400 uppercase text-xs tracking-widest">No heats yet — add riders and generate</p>}
          </div>
        </div>
      )}

      {msg && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 bg-black text-white font-display font-black uppercase tracking-widest px-6 py-3 z-50" data-testid="setup-toast">
          {msg}
        </div>
      )}
    </div>
  );
}
